import type { ReaderState, ReadingHistoryItem } from "./types";

const API_ORIGIN = (import.meta.env.VITE_POETRY_API_ORIGIN || "https://corpus.xhalo.co").replace(/\/+$/, "");

type RemoteState = {
  schemaVersion: 2;
  preferences: ReaderState["preferences"] | null;
  favorites: string[];
  history: ReadingHistoryItem[];
  serverUpdatedAt: string | null;
};

export function mergeReaderState(local: ReaderState, remote: RemoteState): ReaderState {
  const localWinsPreferences = !remote.preferences || !remote.serverUpdatedAt || local.updatedAt >= remote.serverUpdatedAt;
  const historyByWork = new Map<string, ReadingHistoryItem>();
  for (const item of [...remote.history, ...local.history]) {
    const current = historyByWork.get(item.workId);
    if (!current || item.updatedAt > current.updatedAt) historyByWork.set(item.workId, item);
  }
  return {
    schemaVersion: 2,
    preferences: localWinsPreferences ? local.preferences : remote.preferences!,
    favorites: [...new Set([...remote.favorites, ...local.favorites])].slice(-1000),
    history: [...historyByWork.values()].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt)).slice(0, 100),
    updatedAt: new Date().toISOString(),
  };
}

export async function readRemoteState(): Promise<RemoteState> {
  const response = await fetch(`${API_ORIGIN}/v1/user/poetry/state`, { credentials: "include", headers: { accept: "application/json" } });
  const payload = await response.json().catch(() => null) as { ok?: boolean; data?: RemoteState; error?: { message?: string } } | null;
  if (!response.ok || !payload?.ok || !payload.data) throw new Error(payload?.error?.message || "Poetry sync unavailable");
  return payload.data;
}

export async function writeRemoteState(state: ReaderState, uiLocale: "zh" | "en", corpus: "zh" | "en") {
  const response = await fetch(`${API_ORIGIN}/v1/user/poetry/state`, {
    method: "PUT", credentials: "include", headers: { accept: "application/json", "content-type": "application/json" },
    body: JSON.stringify({ ...state, preferences: { ...state.preferences, uiLocale, corpus }, clientUpdatedAt: state.updatedAt }),
  });
  if (!response.ok) throw new Error("Poetry sync unavailable");
}
